/*
 * @Description: 个人轨迹回放 — 播放计算（扁平点列 / 时间范围 / 时刻插值）
 */
import { TimelineData, parseLngLat } from '../../types/cockpit.types'

export interface FlatPoint {
  time: number
  lng: number
  lat: number
}

/** 相邻点超过此间隔视为中断：播放点保持在前一点，不跨中断插值 */
const GAP_HOLD_MS = 10 * 60 * 1000

/**
 * 从 timeline 的实线 move 段抽出按时间升序的扁平点列（播放点沿此移动）。
 */
export function buildFlatTrackPoints(
  timeline: TimelineData | null
): FlatPoint[] {
  if (!timeline) return []
  const points: FlatPoint[] = []
  timeline.events.forEach((e) => {
    if (e.type === 'move' && e.lineType === 'solid' && e.points) {
      e.points.forEach((p) => {
        const ll = parseLngLat(p.location)
        if (ll) points.push({ time: p.locateTime, lng: ll.lng, lat: ll.lat })
      })
    }
  })
  points.sort((a, b) => a.time - b.time)
  return points
}

/**
 * 轨迹时间范围 [start, end]：取所有事件的首末时间（含中断段），与时间轴一致。
 */
export function getTrackRange(timeline: TimelineData | null): {
  start: number
  end: number
} {
  if (!timeline || timeline.events.length === 0) return { start: 0, end: 0 }
  let start = Infinity
  let end = -Infinity
  timeline.events.forEach((e) => {
    if (e.type === 'move') {
      start = Math.min(start, e.startTime)
      end = Math.max(end, e.endTime)
    } else {
      start = Math.min(start, e.time)
      end = Math.max(end, e.time)
    }
  })
  if (start === Infinity) return { start: 0, end: 0 }
  return { start, end }
}

/**
 * 取 T 时刻的播放点位置（在扁平点列上线性插值；跨中断则保持前一点）。
 *
 * @param flat - 时间升序扁平点列
 * @param t - 目标时刻（unix 毫秒）
 * @returns 经纬度；点列为空返回 null
 */
export function positionAt(
  flat: FlatPoint[],
  t: number
): { lng: number; lat: number } | null {
  if (flat.length === 0) return null
  if (t <= flat[0].time) return { lng: flat[0].lng, lat: flat[0].lat }
  const last = flat[flat.length - 1]
  if (t >= last.time) return { lng: last.lng, lat: last.lat }

  let i = 0
  for (; i < flat.length - 1; i++) {
    if (flat[i].time <= t && t < flat[i + 1].time) break
  }
  const a = flat[i]
  const b = flat[i + 1]
  const dt = b.time - a.time
  // 中断段：保持在前一点
  if (dt > GAP_HOLD_MS) return { lng: a.lng, lat: a.lat }
  const ratio = dt > 0 ? (t - a.time) / dt : 0
  return {
    lng: a.lng + (b.lng - a.lng) * ratio,
    lat: a.lat + (b.lat - a.lat) * ratio
  }
}
